import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Row, Col, Divider, Button, Tabs, Skeleton, notification } from "antd";
import styles from "./GroceryDelivery.module.scss";
import textStyles from "../../Shared/Styles/TextStyles.module.scss";
import buttonStyles from "../../Shared/Styles/ButtonStyles.module.scss";
import dividerStyles from "../../Shared/Styles/DividerStyles.module.scss";
import scrollStyles from "../../Shared/Styles/ScrollBarStyles.module.scss";
import closeIcon from "../../../assets/images/closeIconGrey.svg";
import {
  CachedGroceryCartItem,
  CartGroceryItem,
  CategorizedGroceryItems,
  defaultCartCostDetails,
  GroceryCategory,
  GroceryItem,
  ShoppingCartDetails,
  ShoppingCartGroceryItems,
} from "../../../types/GroceryTypes";
import Spinner from "../../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import { useGroceryCtx } from "../../../context/GroceryContext";
import GroceryItemView from "./GroceryItemView";
import DeliveryAddressDrawer from "../Schedule/DeliveryAddressDrawer";
import ScheduleGroceryDrawer from "../Schedule/ScheduleGroceryDrawer";
import { useWindowSize } from "../../../hooks/useWindowSize";
import GroceryShopping from "../GroceryShopping/GroceryShopping";
import FloatingCartButton from "./FloatingCartButton";
import ShoppingCartDrawer from "../ShoppingCart/ShoppingCartDrawer";
import { leftOutlinedIcon } from "../../../assets/images";
import ChangeDeliveryAddressWarningModal from "../Modals/ChangeDeliveryAddressWarningModal";
import { batchFetchImageUrls } from "../../../helpers/personHelpers";
import {
  getTemporaryGroceryCart,
  updateGroceryCartDetails,
} from "../../../services/GroceryService";
import { usePersonSummaryCtx } from "../../../context/PersonSummaryContext";

type Props = {
  groceryCategories: GroceryCategory[];
  groceries: CategorizedGroceryItems[];
  isLoadingGroceries: boolean;
};

function GroceryDelivery({
  groceryCategories,
  groceries,
  isLoadingGroceries,
}: Props) {
  const [activeTab, setActiveTab] = useState<string>("0");
  const [
    isOpenDeliveryAddressDrawer,
    setIsOpenDeliveryAddressDrawer,
  ] = useState<boolean>(false);
  const [
    isOpenScheduleGroceryDrawer,
    setIsOpenScheduleGroceryDrawer,
  ] = useState<boolean>(false);
  const [isGroceryItemsMobileView, setIsGroceryItemsMobileView] = useState<
    boolean
  >(false);

  const { width } = useWindowSize();
  const isMobileView = width <= 992;

  const { caregiverSummaryInfo } = usePersonSummaryCtx();
  const {
    selectedGroceryCategoryIdsCtx,
    deliveryAddressCtx,
    cachedGroceryCartItemsCtx,
    selectedDeliveryScheduleCtx,
    isDeliveryScheduledCtx,
  } = useGroceryCtx();
  const [isDeliveryScheduled] = isDeliveryScheduledCtx;
  const [, setSelectedGroceryCategoryIds] = selectedGroceryCategoryIdsCtx;
  const [cachedGroceryCartItems] = cachedGroceryCartItemsCtx;
  const [deliveryAddress] = deliveryAddressCtx;
  const [selectedDeliverySchedule] = selectedDeliveryScheduleCtx;
  const { personId } = useParams<{ personId: string }>();

  const totalGroceryItemCount: number =
    cachedGroceryCartItems?.reduce(
      (total: number, item: { groceryId: number; quantity: number }) =>
        total + item.quantity,
      0
    ) ?? 0;
  const history = useHistory();
  const [isOpenShoppingCartDrawer, setIsOpenShoppingCartDrawer] = useState<
    boolean
  >(false);
  const [shoppingCartDetails, setShoppingCartDetails] = useState<
    ShoppingCartDetails
  >({ groceryItems: [], costDetails: defaultCartCostDetails() });
  const [isShoppingCartLoading, setIsShoppingCartLoading] = useState<boolean>(
    false
  );

  const { TabPane } = Tabs;

  function handleBack() {
    if (isGroceryItemsMobileView) setIsGroceryItemsMobileView(false);
    else history.push("/DashBoard");
  }

  function handleTabChange(activeKey: string): void {
    setActiveTab(activeKey);
    if (activeKey === "0")
      setSelectedGroceryCategoryIds(
        groceryCategories.map((category) => category.id)
      );
    else setSelectedGroceryCategoryIds([Number(activeKey)]);
  }

  function handleChangeDeliveryAddress(): void {
    setIsOpenDeliveryAddressDrawer(true);
  }

  function handleDisplayShoppingCart() {
    !isDeliveryScheduled
      ? setIsOpenScheduleGroceryDrawer(true)
      : setIsOpenShoppingCartDrawer(true);
  }

  async function fetchShoppingCartInformation() {
    try {
      setIsShoppingCartLoading(true);
      const tempGroceryCart = await getTemporaryGroceryCart(Number(personId));

      if (tempGroceryCart.getGroceryCarts?.length > 0) {
        const shoppingCartResults = await Promise.all([
          fetchShoppingCartItems(tempGroceryCart.getGroceryCarts),
          fetchShoppingCartCostDetails(tempGroceryCart.getGroceryCarts),
        ]);
        setShoppingCartDetails({
          groceryItems: shoppingCartResults[0],
          costDetails: shoppingCartResults[1],
        });
      } else {
        setShoppingCartDetails({
          groceryItems: [],
          costDetails: defaultCartCostDetails(),
        });
      }
    } catch {
      notification.error({
        message: "Failed to fetch shopping cart items.",
      });
    } finally {
      setIsShoppingCartLoading(false);
    }
  }

  async function fetchShoppingCartItems(
    groceryCartItems: CachedGroceryCartItem[]
  ) {
    try {
      const imageKeys = groceryCartItems.reduce(
        (acc: string[], groceryItem: CachedGroceryCartItem) => {
          if (groceryItem.imageKey) {
            acc.push(groceryItem.imageKey);
          }
          return acc;
        },
        []
      );

      let imageUrlsMap: Map<string, string>;
      if (imageKeys.length) {
        imageUrlsMap = await batchFetchImageUrls({
          folderName: "ccp",
          imageKeys,
        });
      }

      const categorizedGroceries: ShoppingCartGroceryItems[] = Object.values(
        groceryCartItems.reduce(
          (
            acc: Record<string, ShoppingCartGroceryItems>,
            groceryItem: CartGroceryItem
          ) => {
            const { categoryName } = groceryItem;
            if (!acc[categoryName]) {
              acc[categoryName] = {
                categoryName,
                groceryItems: [],
              };
            }
            acc[categoryName].groceryItems.push({
              ...groceryItem,
              imageUrl:
                groceryItem.imageKey && imageUrlsMap?.get(groceryItem.imageKey),
            });
            return acc;
          },
          {} as Record<string, ShoppingCartGroceryItems>
        )
      );
      return categorizedGroceries;
    } catch {
      notification.error({
        message: "Failed to fetch shopping cart items.",
      });
      return [];
    }
  }

  async function fetchShoppingCartCostDetails(
    groceryCartItems: CachedGroceryCartItem[]
  ) {
    try {
      const payload = {
        organizationId: deliveryAddress.organizationId,
        serviceUserPersonId: Number(personId),
        caregiverPersonId: caregiverSummaryInfo.personId,
        deliveryDate: selectedDeliverySchedule.date,
        promoCode: "",
        groceryCarts: groceryCartItems,
      };
      const costDetails = await updateGroceryCartDetails(payload);
      return costDetails;
    } catch {
      notification.error({
        message: "Failed to fetch cost details of the cart.",
      });
      return defaultCartCostDetails();
    }
  }
  return (
    <>
      {!isGroceryItemsMobileView && (
        <>
          <Row justify="space-between" className={styles.groceryDeliveryHeader}>
            <Col className={textStyles.normalText20}>Grocery Delivery</Col>
            <Col>
              <Button className={buttonStyles.iconBtn} onClick={handleBack}>
                <img src={closeIcon} alt="go_back" />
              </Button>
            </Col>
          </Row>

          <Divider className={dividerStyles.solidBrandingDivider} />
        </>
      )}

      <Row
        className={`${styles.groceryDeliveryBody} ${
          isMobileView && isGroceryItemsMobileView
            ? styles.whiteBackground
            : styles.greyBackground
        }`}
      >
        <Col xs={0} lg={1} />
        <Col xs={isMobileView && isGroceryItemsMobileView ? 0 : 24} lg={6}>
          <GroceryShopping
            setIsGroceryItemsMobileView={setIsGroceryItemsMobileView}
          />
        </Col>

        <Col
          xs={isMobileView && isGroceryItemsMobileView ? 24 : 0}
          lg={16}
          className={styles.groceriesView}
        >
          <Row
            justify={isMobileView ? "start" : "space-between"}
            className={styles.headerContent}
          >
            <Col xs={2} lg={0}>
              <Button className={buttonStyles.iconBtn} onClick={handleBack}>
                <img src={leftOutlinedIcon} alt="go_back" />
              </Button>
            </Col>

            <Col xs={22} lg={""}>
              <Row className={textStyles.normalText20}>Groceries</Row>

              <Row className={textStyles.normalText14}>Delivery address</Row>
              {!deliveryAddress?.zip ? (
                <Skeleton active />
              ) : (
                <Row
                  justify={isMobileView ? "space-between" : "start"}
                  align="bottom"
                  gutter={[7, 0]}
                >
                  <Col className={styles.tertiaryNormalText12}>
                    {deliveryAddress.address1}, {deliveryAddress.city},{" "}
                    {deliveryAddress.state} {deliveryAddress.zip}
                  </Col>

                  <Col>
                    <Button
                      type="link"
                      className={`${buttonStyles.linkBtn} ${textStyles.normalText14}`}
                      onClick={handleChangeDeliveryAddress}
                    >
                      Change
                    </Button>
                  </Col>
                </Row>
              )}
            </Col>

            <Col>{/* Filter - TBD - 06.02 */}</Col>
          </Row>

          <Row>
            <Col span={24}>
              <Tabs
                className={`${styles.tabsContainer} ${styles.tabsBtn}`}
                onChange={handleTabChange}
                activeKey={activeTab}
              >
                <TabPane tab="All" key="0">
                  {isLoadingGroceries ? (
                    <Spinner />
                  ) : (
                    groceries.map((grocery) => (
                      <>
                        <Row
                          key={grocery.categoryName}
                          justify="space-between"
                          className={styles.marginUp32}
                        >
                          <Col className={textStyles.normalText20}>
                            {grocery.categoryName}
                          </Col>
                          <Col>
                            <Button
                              className={`${buttonStyles.linkBtn} ${textStyles.normalText14}`}
                              onClick={() =>
                                handleTabChange(
                                  grocery.groceryItems[0].groceryCategoryId.toString()
                                )
                              }
                            >
                              View All
                            </Button>
                          </Col>
                        </Row>
                        <Row className={scrollStyles.horizontalScroll}>
                          {grocery.groceryItems.map((groceryItem) => (
                            <GroceryItemView
                              key={groceryItem.groceryId}
                              align="vertical"
                              groceryItem={groceryItem}
                              setIsOpenScheduleGroceryDrawer={
                                setIsOpenScheduleGroceryDrawer
                              }
                            />
                          ))}
                        </Row>
                      </>
                    ))
                  )}
                </TabPane>

                {groceryCategories.map((category) => (
                  <TabPane tab={category.name} key={category.id.toString()}>
                    {isLoadingGroceries ? (
                      <Spinner />
                    ) : (
                      groceries.map((grocery) => (
                        <Row key={grocery.categoryName}>
                          {grocery.groceryItems.map((groceryItem) => (
                            <>
                              <Col xs={24} lg={11}>
                                <GroceryItemView
                                  key={groceryItem.groceryId}
                                  align="horizontal"
                                  groceryItem={groceryItem}
                                  setIsOpenScheduleGroceryDrawer={
                                    setIsOpenScheduleGroceryDrawer
                                  }
                                />
                              </Col>
                              <Col xs={0} lg={1} />
                            </>
                          ))}
                        </Row>
                      ))
                    )}
                  </TabPane>
                ))}
              </Tabs>
            </Col>
          </Row>
        </Col>
        <Col xs={0} lg={1} />

        <FloatingCartButton
          itemCount={totalGroceryItemCount}
          onClick={handleDisplayShoppingCart}
        />
      </Row>

      {isOpenScheduleGroceryDrawer && (
        <ScheduleGroceryDrawer
          isVisible={isOpenScheduleGroceryDrawer}
          setIsVisible={setIsOpenScheduleGroceryDrawer}
          setIsOpenDeliveryAddressDrawer={setIsOpenDeliveryAddressDrawer}
        />
      )}

      {isOpenDeliveryAddressDrawer && (
        <DeliveryAddressDrawer
          cartItemsCount={totalGroceryItemCount}
          isDrawerVisible={isOpenDeliveryAddressDrawer}
          setIsDrawerVisible={setIsOpenDeliveryAddressDrawer}
        />
      )}

      {isOpenShoppingCartDrawer && (
        <ShoppingCartDrawer
          isDrawerVisible={isOpenShoppingCartDrawer}
          setIsDrawerVisible={setIsOpenShoppingCartDrawer}
          isShoppingCartLoading={isShoppingCartLoading}
          shoppingCartDetails={shoppingCartDetails}
          fetchShoppingCartInformation={fetchShoppingCartInformation}
        />
      )}
    </>
  );
}

export default GroceryDelivery;
